/**
 * Deepgram TTS Service (Client-Side)
 * 
 * Text-to-speech service using Deepgram Aura voices.
 * Calls Firebase Cloud Function to generate speech.
 * 
 * Available Aura voices:
 * - Female: asteria, luna, stella, athena, hera
 * - Male: orion, arcas, perseus, angus, orpheus, helios, zeus
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  TTS_PLAYBACK_POST_END_PAD_MS,
  TTS_AUDIO_TAIL_EPSILON_SEC,
  TTS_PREMATURE_TAIL_POLL_MS,
  TTS_PREMATURE_TAIL_MAX_EXTRA_MS,
} from '../ttsService';

// Available Aura voices
export const AURA_VOICES = {
  // Female voices
  asteria: { gender: 'female', accent: 'american', description: 'Default female voice' },
  luna: { gender: 'female', accent: 'american', description: 'Soft female voice' },
  stella: { gender: 'female', accent: 'american', description: 'Clear female voice' },
  athena: { gender: 'female', accent: 'british', description: 'British female voice' },
  hera: { gender: 'female', accent: 'american', description: 'Warm female voice' },
  
  // Male voices
  orion: { gender: 'male', accent: 'american', description: 'Default male voice' },
  arcas: { gender: 'male', accent: 'american', description: 'Deep male voice' },
  perseus: { gender: 'male', accent: 'american', description: 'Authoritative male voice' },
  angus: { gender: 'male', accent: 'irish', description: 'Irish male voice' },
  orpheus: { gender: 'male', accent: 'american', description: 'Melodic male voice' },
  helios: { gender: 'male', accent: 'british', description: 'British male voice' },
  zeus: { gender: 'male', accent: 'american', description: 'Commanding male voice' },
};

// Default configuration
// Deepgram Aura encoding options: linear16, mulaw, alaw, mp3, opus, flac, aac
// Deepgram Aura container options: wav, ogg, none
// We use linear16 encoding with wav container for best browser compatibility
const DEFAULT_CONFIG = {
  voice: 'asteria',
  // Don't specify encoding here - let server use its defaults (linear16 + wav container)
};

/**
 * Deepgram TTS Service Class
 */
class DeepgramTTSService {
  constructor() {
    this.config = { ...DEFAULT_CONFIG };
    this.audioContext = null;
    this.currentAudio = null;
    this.isSpeaking = false;
    
    // Callbacks
    this.onStart = null;
    this.onEnd = null;
    this.onError = null;
    /** Post-`ended` pad (same rationale as OpenAI ttsService playAudio). */
    this._postEndPadTimer = null;
    this._completePostEndPlay = null;
    this._tailRecoveryPollId = null;
    this._playbackToken = 0;
    
    // Firebase function
    this.functions = getFunctions();
    this.deepgramTTSFn = httpsCallable(this.functions, 'deepgramTTS');
    
    // Audio cache (voice+text -> audio blob URL)
    this.audioCache = new Map();
    this.maxCacheSize = 50;
  }

  _clearTailRecoveryPoll() {
    if (this._tailRecoveryPollId !== null) {
      clearInterval(this._tailRecoveryPollId);
      this._tailRecoveryPollId = null;
    }
  }

  /**
   * Configure the service
   * @param {Object} config - { voice, encoding }
   */
  configure(config = {}) {
    if (config.voice && !AURA_VOICES[config.voice]) {
      console.warn(`[DeepgramTTS] Unknown voice: ${config.voice}, using default`);
      config.voice = DEFAULT_CONFIG.voice;
    }
    
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * Set callback handlers
   * @param {Object} handlers - { onStart, onEnd, onError }
   */
  setHandlers(handlers) {
    if (handlers.onStart) this.onStart = handlers.onStart;
    if (handlers.onEnd) this.onEnd = handlers.onEnd;
    if (handlers.onError) this.onError = handlers.onError;
  }

  /**
   * Get cache key for a text/voice/language combination
   * @param {string} text - Text to speak
   * @param {string} voice - Voice to use
   * @param {string} [language] - Language code (so same text in different languages gets different audio)
   * @returns {string} Cache key
   */
  _getCacheKey(text, voice, language = '') {
    const lang = (language && language.split(/[-_]/)[0]) || '';
    return `${voice}:${lang || 'en'}:${text}`;
  }

  /**
   * Check if audio is cached
   * @param {string} text - Text to check
   * @param {string} voice - Voice to check
   * @returns {boolean}
   */
  isCached(text, voice = this.config.voice, language = '') {
    return this.audioCache.has(this._getCacheKey(text, voice, language));
  }

  /**
   * Generate speech from text
   * @param {string} text - Text to speak
   * @param {Object} options - { voice, language, useCache }
   * @returns {Promise<string>} Blob URL of generated audio
   */
  async generateSpeech(text, options = {}) {
    const voice = options.voice || this.config.voice;
    const language = options.language; // Language code (e.g., 'fr', 'es-MX')
    const useCache = options.useCache !== false;
    
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      throw new Error('Text cannot be empty');
    }

    const cacheKey = this._getCacheKey(text, voice, language);

    // Check cache first
    if (useCache && this.audioCache.has(cacheKey)) {
      return this.audioCache.get(cacheKey);
    }

    try {
      
      // Call Firebase function
      // Pass language so server can select appropriate voice for the target language
      const result = await this.deepgramTTSFn({
        text,
        voice,
        language, // Server will use this to select a native voice if no specific voice is provided
        options: {},
      });

      const { audio, contentType } = result.data;

      if (!audio) {
        throw new Error('No audio data received');
      }

      // Convert base64 to blob
      const audioBlob = this._base64ToBlob(audio, contentType);
      const blobUrl = URL.createObjectURL(audioBlob);

      // Cache the result
      if (useCache) {
        this._addToCache(cacheKey, blobUrl);
      }

      return blobUrl;

    } catch (error) {
      console.error('[DeepgramTTS] Error generating speech:', error);
      throw error;
    }
  }

  /**
   * Speak text (generate and play)
   * @param {string} text - Text to speak
   * @param {Object} options - { voice, useCache }
   * @returns {Promise<void>}
   */
  async speak(text, options = {}) {
    
    if (this.isSpeaking) {
      this.stop();
    }

    try {
      const blobUrl = await this.generateSpeech(text, options);
      
      await this.playAudio(blobUrl);
    } catch (error) {
      console.error('[DeepgramTTS] speak() error:', error);
      this.onError?.(error);
      throw error;
    }
  }

  /**
   * Play audio from URL
   * @param {string} audioUrl - Blob URL or regular URL
   * @returns {Promise<void>}
   */
  playAudio(audioUrl) {
    return new Promise((resolve, reject) => {
      this.stop();
      const playbackToken = ++this._playbackToken;

      this.currentAudio = new Audio(audioUrl);
      
      this.currentAudio.onplay = () => {
        if (playbackToken !== this._playbackToken) return;
        this.isSpeaking = true;
        this.onStart?.();
      };
      
      // Ensure audio is not muted and volume is up
      this.currentAudio.volume = 1.0;
      this.currentAudio.muted = false;

      this.currentAudio.onended = () => {
        if (playbackToken !== this._playbackToken) return;
        const runCompletion = () => {
          if (playbackToken !== this._playbackToken) return;
          if (this._postEndPadTimer) {
            clearTimeout(this._postEndPadTimer);
            this._postEndPadTimer = null;
          }
          this._completePostEndPlay = null;
          this._clearTailRecoveryPoll();
          this.isSpeaking = false;
          this.currentAudio = null;
          this.onEnd?.();
          resolve();
        };

        const queuePostEndPad = () => {
          this._completePostEndPlay = runCompletion;
          this._postEndPadTimer = setTimeout(runCompletion, TTS_PLAYBACK_POST_END_PAD_MS);
        };

        const el = this.currentAudio;
        const dur = el?.duration;
        const ct = el?.currentTime;
        const eps = TTS_AUDIO_TAIL_EPSILON_SEC;
        const looksTruncated =
          el &&
          Number.isFinite(dur) &&
          dur > eps &&
          Number.isFinite(ct) &&
          ct < dur - eps;

        if (looksTruncated) {
          console.warn('[DeepgramTTS] HTMLAudio ended before duration tail; recovering playback', {
            duration: dur,
            currentTime: ct,
          });
          this._clearTailRecoveryPoll();
          void el.play().catch(() => {});
          const started = Date.now();
          const rate = Math.max(0.25, el.playbackRate || 1);
          const maxWaitMs = Math.min(
            TTS_PREMATURE_TAIL_MAX_EXTRA_MS,
            ((dur - ct) / rate) * 1000 + 2500
          );
          this._tailRecoveryPollId = window.setInterval(() => {
            if (this.currentAudio !== el) {
              this._clearTailRecoveryPoll();
              queuePostEndPad();
              return;
            }
            const ct2 = el.currentTime;
            const dur2 = el.duration;
            if (
              !Number.isFinite(dur2) ||
              dur2 <= eps ||
              (Number.isFinite(ct2) && ct2 >= dur2 - eps)
            ) {
              this._clearTailRecoveryPoll();
              queuePostEndPad();
              return;
            }
            if (Date.now() - started > maxWaitMs) {
              console.warn('[DeepgramTTS] Tail recovery timed out; completing TTS', {
                currentTime: ct2,
                duration: dur2,
              });
              this._clearTailRecoveryPoll();
              queuePostEndPad();
            }
          }, TTS_PREMATURE_TAIL_POLL_MS);
          return;
        }

        queuePostEndPad();
      };

      this.currentAudio.onerror = (error) => {
        if (playbackToken !== this._playbackToken) return;
        if (this._postEndPadTimer) {
          clearTimeout(this._postEndPadTimer);
          this._postEndPadTimer = null;
        }
        this._completePostEndPlay = null;
        this._clearTailRecoveryPoll();
        this.isSpeaking = false;
        this.currentAudio = null;
        const err = new Error('Audio playback failed');
        this.onError?.(err);
        reject(err);
      };

      this.currentAudio.play().catch(error => {
        if (playbackToken !== this._playbackToken) return;
        if (this._postEndPadTimer) {
          clearTimeout(this._postEndPadTimer);
          this._postEndPadTimer = null;
        }
        this._completePostEndPlay = null;
        this._clearTailRecoveryPoll();
        this.isSpeaking = false;
        this.currentAudio = null;
        this.onError?.(error);
        reject(error);
      });
    });
  }

  /**
   * Stop current audio playback
   */
  stop() {
    this._playbackToken += 1;
    this._clearTailRecoveryPoll();
    if (this._postEndPadTimer) {
      clearTimeout(this._postEndPadTimer);
      this._postEndPadTimer = null;
    }
    if (typeof this._completePostEndPlay === 'function') {
      const finish = this._completePostEndPlay;
      this._completePostEndPlay = null;
      finish();
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
      this.isSpeaking = false;
    }
  }

  /**
   * Check if currently speaking
   * @returns {boolean}
   */
  speaking() {
    return this.isSpeaking;
  }

  /**
   * Convert base64 to Blob
   * @param {string} base64 - Base64 encoded data
   * @param {string} contentType - MIME type
   * @returns {Blob}
   */
  _base64ToBlob(base64, contentType = 'audio/wav') {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: contentType });
  }

  /**
   * Add item to cache with LRU eviction
   * @param {string} key - Cache key
   * @param {string} value - Blob URL
   */
  _addToCache(key, value) {
    // Evict oldest if at capacity
    if (this.audioCache.size >= this.maxCacheSize) {
      const firstKey = this.audioCache.keys().next().value;
      const oldUrl = this.audioCache.get(firstKey);
      URL.revokeObjectURL(oldUrl);
      this.audioCache.delete(firstKey);
    }
    
    this.audioCache.set(key, value);
  }

  /**
   * Clear audio cache
   */
  clearCache() {
    this.audioCache.forEach(url => URL.revokeObjectURL(url));
    this.audioCache.clear();
  }

  /**
   * Get available voices
   * @returns {Object} Available voices
   */
  getVoices() {
    return { ...AURA_VOICES };
  }

  /**
   * Get voices by gender
   * @param {'male' | 'female'} gender - Gender filter
   * @returns {Object} Filtered voices
   */
  getVoicesByGender(gender) {
    return Object.fromEntries(
      Object.entries(AURA_VOICES).filter(([, info]) => info.gender === gender)
    );
  }

  /**
   * Get current configuration
   * @returns {Object} Current config
   */
  getConfig() {
    return { ...this.config };
  }

  /**
   * Get service status
   * @returns {Object} Status info
   */
  getStatus() {
    return {
      isSpeaking: this.isSpeaking,
      config: this.config,
      cacheSize: this.audioCache.size,
    };
  }
}

// Export singleton instance
export const deepgramTTSService = new DeepgramTTSService();

// Export class for testing
export { DeepgramTTSService };

export default deepgramTTSService;
